import { ElementType } from 'react';
import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { PolymorphicComponentPropWithoutRef, Text, TextProps } from '@frontend/design-system';

const dividerWithLabelComponentStyle1 = css`
  display: flex;
  gap: ${theme.spacing(1)};
  align-items: center;
  width: 100%;
  margin: var(--divider-with-label--component-inline-expr1);
`;

const dividerWithLabelDividerStyle2 = css`
  flex-grow: 1;
`;

const dividerWithLabelTextStyle3 = css`
  color: var(--divider-with-label--text-inline-color);
`;

const dividerWithLabelDividerStyle4 = css`
  flex-grow: 1;
`;

type DividerProps = {
  color?: string;
  margin?: string | number;
};

type PolymorphicDividerProps<E extends ElementType = 'hr'> = PolymorphicComponentPropWithoutRef<E, DividerProps>;

export const hrStyle = css`
  width: auto;
  border: 0;
  border-top-width: 1px;
  border-top-style: solid;
  border-top-color: var(--hr-style-color, ${theme.colors.neutral10});
  margin-top: 0;
  margin-bottom: 0;
`;

export const getHrStyleVars = (color: string = theme.colors.neutral10) =>
  ({
    '--hr-style-color': color,
  } as any);

export const dividerStyle = css`
  width: auto;
  border: 0;
  border-top-width: 1px;
  border-top-style: solid;
  border-top-color: var(--divider-style-color);
  margin-top: 0;
  margin-bottom: 0;

  margin-top: var(--divider-style-expr1, ${theme.spacing(2)});
  margin-bottom: var(--divider-style-expr2, ${theme.spacing(2)});
  width: 100%;
`;

export const getDividerStyleVars = (color: string | undefined, margin: string | number | undefined) =>
  ({
    '--divider-style-color': color,
    '--divider-style-expr1': margin,
    '--divider-style-expr2': margin,
  } as any);

export const Divider = <E extends ElementType = 'hr'>({
  as,
  color,
  margin,
  className,
  ...rest
}: PolymorphicDividerProps<E>) => {
  const Component = as ?? 'hr';
  return <Component {...rest} style={getDividerStyleVars(color, margin)} className={cx(dividerStyle, className)} />;
};

type DividerWithLabelProps = DividerProps & {
  label: string;
  labelSize?: TextProps['size'];
};

type PolymorphicDividerWithLabelProps<E extends ElementType = 'span'> = PolymorphicComponentPropWithoutRef<
  E,
  DividerWithLabelProps
>;

export const DividerWithLabel = <E extends ElementType = 'span'>({
  as,
  color,
  margin,
  label,
  labelSize = 'medium',
  className,
  ...rest
}: PolymorphicDividerWithLabelProps<E>) => {
  const Component = as ?? 'span';
  return (
    <Component
      {...rest}
      style={
        {
          '--divider-with-label--component-inline-expr1': `${margin ?? theme.spacing(2)} 0`,
        } as any
      }
      className={cx(dividerWithLabelComponentStyle1, className)}
    >
      <Divider color={color} className={cx(hrStyle, dividerWithLabelDividerStyle2)} style={getHrStyleVars(color)} />
      <Text
        className={dividerWithLabelTextStyle3}
        style={
          {
            '--divider-with-label--text-inline-color': color,
          } as any
        }
        size={labelSize}
      >
        {label}
      </Text>
      <Divider color={color} className={cx(hrStyle, dividerWithLabelDividerStyle4)} style={getHrStyleVars(color)} />
    </Component>
  );
};
